import {
  GaEventItems,
  OrderAttributesResponse,
  OrderItemPrice,
  OrderItemPriceAvailabilityTotals,
  OrderItemsResponse
} from 'services/cart/types';
import {
  QOCartResponse,
  PriceAvailabilityLegacyItem,
  QOCookieData
} from 'services/priceAvailabilityLegacy/types';
import { OrderItem } from '@cat-ecom/common/utils/productFormUtils';
import { getValueOrElseEmptyString } from 'utils/string';
import { CartItem } from 'hooks/analytics/types';
import { UpdateOrderItemResponse } from 'services/order/types';
import {
  VIEW_CART_EVENT,
  VIEW_ITEM_LIST_EVENT
} from 'constants/analyticsConstants';
import {
  ListData,
  ViewItemListType
} from 'hooks/analytics/use-view-item-list-event';
import {
  PriceData,
  ProductsPriceData,
  SearchAddToCartItemData
} from 'app/[lang]/[esite]/(core)/(search-display)/_components/analytics-listener/types';
import { AddPartToSavedList } from 'services/savedLists/types';
import { normalizedPartNumber } from 'services/cart/utils';

type ViewItemsType = {
  product: OrderItemsResponse | ProductsPriceData;
  name: string;
  index: number;
  discountAmount: number;
  unitPrice: number;
  manufacturer: string;
  categoryArr: string[];
  locationId?: string;
  availabilityMessage: string;
  quantity?: number | string;
  weight?: number | string;
  serialNumber?: string;
  fitmentCheck?: string;
  itemListData?: ListData;
};

export const getAttributeValueForCart = (
  orderItemPriceData: OrderItemPrice[],
  orderItemId: number
) => {
  const itemData = orderItemPriceData.find(
    (item) => item.orderItemId === orderItemId
  );
  const {
    availability,
    priceWithoutGST: {
      unformatted: { unitPrice = 0.0, discountAmount = 0.0, totalPrice = 0.1 }
    }
  } = itemData;
  return {
    availabilityMessage: availability[0].availabilityMessage,
    discountAmount,
    totalPrice,
    unitPrice
  };
};

export const formatPrice = (price: string | number) =>
  typeof price === 'string' ? Number(price.replace(/[$,]/g, '')) : price;

export const getAttributeValueForSearch = (priceData: PriceData) => {
  const {
    availability = [],
    price: { unitPrice = 0.0, discountAmount = 0.0 } = {},
    quantity = 0,
    weight = 0
  } = priceData || {};

  return {
    availabilityMessage: availability
      ? availability[0]?.availabilityMessage
      : null,
    discountAmount: formatPrice(discountAmount),
    unitPrice: formatPrice(unitPrice),
    quantity,
    weight
  };
};

export const getProductAttr = (productsData: Product[], partNumber: string) => {
  const summaryProductData = productsData?.find(
    (item) => item.partNumber === partNumber.trim()
  );
  const categoryArr = summaryProductData?.categoryPath?.split('/') || [];
  return { manufacturer: summaryProductData?.manufacturer, categoryArr };
};

export const getProductAttrSearch = (productData: ProductsPriceData) => {
  const categoryArr =
    productData?.productPriceData?.categoryPath?.split('/') || [];
  return { manufacturer: productData?.manufacturer, categoryArr };
};

export function getOrderSubTotal(items: PriceAvailabilityLegacyItem[]): number {
  const subTotal = items?.reduce((acc, item) => {
    return acc + item?.price?.unformatted?.unitPrice * item.quantity;
  }, 0);
  return Number(subTotal?.toFixed(2));
}

export const getQOEventData = (
  orderId: string,
  orderItems: QOCartResponse,
  formFields: OrderItem[],
  locationId: string,
  qoCookieData: QOCookieData[]
) => {
  const {
    quickOrderItems: { currency, items },
    products
  } = orderItems;

  const cartDetails = {
    cartId: getValueOrElseEmptyString(orderId)
  };

  formFields.reverse();
  const filterPartNumbers = formFields?.map((item: OrderItem) => {
    return {
      partNumber: item?.itemNumber,
      quantity: item?.quantity
    };
  });

  const itemsData = items || filterPartNumbers;

  const orderSubTotal = getOrderSubTotal(items);

  const cartItems = (itemsData as PriceAvailabilityLegacyItem[])?.map(
    (partItem, index) => {
      const { manufacturer = '', categoryArr = [] } = getProductAttr(
        products,
        partItem?.partNumber
      );
      const itemListName = qoCookieData?.find(
        (i) =>
          normalizedPartNumber(i.itemNumber) ===
          normalizedPartNumber(partItem.partNumber)
      )?.page;
      return {
        item_id: partItem?.partNumber,
        item_name: `${partItem?.partNumber}: ${partItem?.partName}`,
        affiliation: undefined,
        coupon: undefined,
        discount: partItem?.price?.unformatted?.discountAmount,
        index: index + 1,
        item_brand: manufacturer || undefined,
        item_category: categoryArr[0] || undefined,
        item_category2: categoryArr[1] || undefined,
        item_category3: categoryArr[2] || undefined,
        item_category4: categoryArr[3] || undefined,
        item_category5: categoryArr[4] || undefined,
        item_list_id: undefined,
        item_list_name: itemListName,
        item_variant: undefined,
        location_id: locationId,
        price: partItem?.price?.unformatted?.unitPrice,
        quantity: partItem?.quantity,
        p_inventory_status: getValueOrElseEmptyString(
          partItem?.availability?.[0]?.availabilityMessage
        ),
        p_weight: partItem?.weight,
        p_serial_number: undefined,
        p_kit_id: null, // BE to send data in get cart details story is open
        p_check_fitment: undefined,
        p_attributes_visible: undefined
      };
    }
  );

  return {
    cartDetails,
    currency,
    orderSubTotal,
    cartItems
  };
};

export const getQuantityChangeEventData = (
  locationId: string,
  itemSummary: Product,
  itemData: UpdateOrderItemResponse | SearchAddToCartItemData
) => {
  const {
    orderId,
    orderItems,
    orderItemPriceAvailabilityTotals: { currency, orderItemPrices }
  } = itemData;

  const cartDetails = {
    cartId: orderId
  };

  const quantity = Number(orderItems?.[0]?.quantity);
  const price = orderItemPrices[0]?.priceWithoutGST?.unformatted?.unitPrice;

  const subTotal = (price * quantity).toFixed(2);

  const categoryArr = itemSummary?.categoryPath?.split('/') || [];

  const items: CartItem = {
    item_id: itemSummary?.partNumber,
    item_name:
      itemSummary && `${itemSummary?.partNumber}: ${itemSummary?.name}`,
    affiliation: undefined,
    coupon: undefined,
    discount: orderItemPrices[0]?.priceWithoutGST?.unformatted?.discountAmount,
    index: 1,
    item_brand: itemSummary?.manufacturer || undefined,
    item_category: categoryArr[0] || undefined,
    item_category2: categoryArr[1] || undefined,
    item_category3: categoryArr[2] || undefined,
    item_category4: categoryArr[3] || undefined,
    item_category5: categoryArr[4] || undefined,
    item_variant: undefined,
    location_id: locationId,
    price,
    quantity: isNaN(quantity) ? undefined : quantity,
    p_inventory_status:
      orderItemPrices[0]?.availability[0]?.availabilityMessage || undefined,
    p_weight: undefined,
    p_serial_number: undefined,
    p_kit_id: null, // BE to send data in get cart deatils story is open
    p_check_fitment: undefined,
    p_attributes_visible: undefined
  };

  return {
    cartDetails,
    currency,
    cartItem: [items],
    value: isNaN(Number(subTotal)) ? undefined : Number(subTotal)
  };
};

export const getProductsViewItems = ({
  product,
  name,
  index,
  discountAmount,
  unitPrice,
  manufacturer,
  categoryArr,
  locationId,
  availabilityMessage,
  quantity,
  weight,
  serialNumber,
  fitmentCheck,
  itemListData
}: ViewItemsType) => {
  const items = {
    item_id: product.partNumber,
    item_name: `${product.partNumber}: ${name}`,
    affiliation: 'PCC',
    coupon: undefined,
    discount: discountAmount ?? 0,
    index: index + 1,
    item_brand: manufacturer ?? undefined,
    item_category: categoryArr[0] ?? undefined,
    item_category2: categoryArr[1] ?? undefined,
    item_category3: categoryArr[2] ?? undefined,
    item_category4: categoryArr[3] ?? undefined,
    item_category5: categoryArr[4] ?? undefined,
    item_list_id: itemListData?.item_list_id ?? undefined,
    item_list_name: itemListData?.item_list_name ?? undefined,
    item_variant: undefined,
    location_id: locationId ?? undefined,
    price: unitPrice ?? 0,
    quantity: Number(quantity),
    p_inventory_status: availabilityMessage ?? undefined,
    p_weight: weight?.toString() ?? undefined,
    p_serial_number: serialNumber ?? undefined,
    p_kit_id: 0,
    p_check_fitment: fitmentCheck ?? undefined,
    p_attributes_visible: undefined
  };

  return items;
};

export const getDataForOrderItems = (
  orderItems: OrderItemsResponse[],
  orderItemPriceAvailabilityTotals: OrderItemPriceAvailabilityTotals,
  products: Product[],
  locationId: string,
  orderAttributes: OrderAttributesResponse
) => {
  const items = orderItems?.map((product, index) => {
    const { manufacturer, categoryArr } = getProductAttr(
      products,
      product.partNumber
    );

    const { availabilityMessage, discountAmount, unitPrice } =
      getAttributeValueForCart(
        orderItemPriceAvailabilityTotals?.orderItemPrices || [],
        product?.orderItemId
      );

    const params = {
      name: product.description,
      product,
      index,
      discountAmount,
      unitPrice,
      manufacturer,
      categoryArr,
      locationId,
      availabilityMessage,
      quantity: product.quantity,
      weight: product.weight,
      serialNumber: orderAttributes?.equipment?.serialNumber
    };

    const item: GaEventItems = getProductsViewItems(params);
    return item;
  });

  return items;
};

export const getDataForSearchProducts = (
  products: ProductsPriceData[],
  itemListData: ListData,
  locationId: string
) => {
  const items = products?.map((product, index) => {
    const { manufacturer, categoryArr } = getProductAttrSearch(product);

    const { availabilityMessage, discountAmount, unitPrice, quantity, weight } =
      getAttributeValueForSearch(product.productPriceData);

    const params = {
      name: product.name,
      product,
      index,
      discountAmount,
      unitPrice,
      manufacturer,
      categoryArr,
      locationId,
      availabilityMessage,
      quantity,
      weight,
      serialNumber: product.serialNumber,
      fitmentCheck: product.fitmentCheck,
      itemListData
    };

    const item: GaEventItems = getProductsViewItems(params);
    return item;
  });

  return items;
};

export const getViewItems = ({
  cartId,
  cartItemsData,
  products,
  locationId,
  type,
  isProductsData = false,
  itemListData
}: ViewItemListType) => {
  type Ecommerce = {
    items: GaEventItems[];
    item_list_id?: string;
    item_list_name?: string;
    currency?: unknown;
    value?: number | string;
  };

  const cartDetails = {
    cartID: cartId || undefined
  };

  const { orderItemPriceAvailabilityTotals, orderItems, orderAttributes } =
    cartItemsData || {};

  const items = !isProductsData
    ? getDataForOrderItems(
        orderItems,
        orderItemPriceAvailabilityTotals,
        products,
        locationId,
        orderAttributes
      )
    : getDataForSearchProducts(products, itemListData, locationId);

  const ecommerce: Ecommerce = {
    items: items
  };

  if (type === VIEW_CART_EVENT) {
    ecommerce.currency = getValueOrElseEmptyString(
      orderItemPriceAvailabilityTotals?.currency || ''
    );
    ecommerce.value =
      orderItemPriceAvailabilityTotals?.orderTotals?.unformatted
        ?.orderSubTotalWithGST || '';
  } else {
    ecommerce.item_list_id = itemListData?.item_list_id ?? undefined;
    ecommerce.item_list_name = itemListData?.item_list_name ?? undefined;
  }

  return {
    event:
      type === VIEW_ITEM_LIST_EVENT ? VIEW_ITEM_LIST_EVENT : VIEW_CART_EVENT,
    cartDetails,
    ecommerce
  };
};

export const getViewItem = ({
  cartId,
  products,
  locationId,
  type,
  itemListData
}: ViewItemListType) => {
  type Ecommerce = {
    items: GaEventItems[];
    item_list_id?: string;
    item_list_name?: string;
    currency?: unknown;
    value?: number | string;
  };
  const cartDetails = {
    cartID: cartId || undefined
  };
  const items = getDataForSearchProducts(products, itemListData, locationId);
  const [
    {
      productPriceData: {
        currency = '',
        price: { unitPrice }
      }
    }
  ] = products;
  const ecommerce: Ecommerce = {
    items,
    currency,
    value: formatPrice(unitPrice ?? 0.0)
  };
  return {
    event: type,
    cartDetails,
    ecommerce
  };
};

export const getAddToWishlistEventData = (
  orderId?: string,
  currencyCode?: string,
  wishlistItems?: AddPartToSavedList[],
  locationId?: string,
  products?: Product[],
  items?: PriceAvailabilityLegacyItem[]
) => {
  const currency = currencyCode;
  const cartDetails = {
    cartId: getValueOrElseEmptyString(orderId)
  };

  const filterPartNumbers = wishlistItems?.map((item: AddPartToSavedList) => {
    return {
      partNumber: item?.partNumber,
      partName: item?.name,
      quantity: item?.quantity,
      price: 0
    };
  });

  const itemsData = items?.length > 0 ? items : filterPartNumbers;
  const orderSubTotal = items?.length > 0 ? getOrderSubTotal(items) : 0;
  const ItemData = itemsData?.map((partItem, index) => {
    const { manufacturer = '', categoryArr = [] } = getProductAttr(
      products,
      partItem?.partNumber
    );
    return {
      item_id: partItem?.partNumber,
      item_name: `${partItem?.partNumber}: ${partItem?.partName}`,
      affiliation: '',
      coupon: '',
      discount: partItem?.price?.unformatted?.discountAmount || 0,
      index: index + 1,
      item_brand: manufacturer || '',
      item_category: categoryArr[0] || '',
      item_category2: categoryArr[1] || '',
      item_category3: categoryArr[2] || '',
      item_category4: partItem?.partNumber || '',
      item_category5: categoryArr[3] || '',
      item_list_id: '',
      item_list_name: '',
      item_variant: '',
      location_id: locationId,
      price: partItem?.price?.unformatted?.discountedPrice || 0,
      quantity: partItem?.quantity,
      p_inventory_status: '',
      p_weight: partItem?.weight || '',
      p_serial_number: '',
      p_kit_id: '',
      p_check_fitment: '',
      p_attributes_visible: ''
    };
  });

  return {
    cartDetails,
    currency,
    orderSubTotal,
    ItemData
  };
};

export const buildEcommerceEvent = () => {
  return {
    ecommerce: null
  };
};
